import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import {
    Box,
    Button,
    Link,
    List,
    ListItem,
    TablePagination,
    Typography,
    makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import moment from 'moment';
import Modal from '../../components/Modals/Modal';
import { Spinner } from '../../components/UI';
import { EmptyIcon } from '../../assets/svg';
import { downloadData } from '../../utils/helper';
import {
    fetchAgencyBulkUploadList,
    fetchAgencyUploadChunks,
    fetchAgencyChunkResult,
} from '../../store/actions/agencyActionCreator';

const useStyles = makeStyles(() => ({
    listItem: {
        padding: '8px 16px 8px 16px !important',
        marginBottom: '8px !important',
        border: '1px solid #ebedf8 !important',
        borderRadius: '8px !important',
    },
    listItemHeader: {
        border: '0px !important',
        marginBottom: '8px !important',
        color: '#525050 !important',
        fontWeight: 'bold !important',
    },
}));

const STATUS_COLORS = {
    PENDING: { bg: '#f5f5f5', color: '#757575' },
    PROCESSING: { bg: '#e3f2fd', color: '#1565c0' },
    COMPLETED: { bg: '#e8f5e9', color: '#2e7d32' },
    FAILED: { bg: '#ffebee', color: '#c62828' },
};

const StatusChip = ({ status }) => {
    const style = STATUS_COLORS[status] ?? STATUS_COLORS.PENDING;
    return (
        <Typography
            style={{
                display: 'inline-block',
                fontSize: '13px',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: style.bg,
                color: style.color,
                whiteSpace: 'nowrap',
            }}
        >
            {status ?? '-'}
        </Typography>
    );
};

const DateTimeCell = ({ value }) => (
    <>
        <Typography style={{ fontSize: '13px' }}>
            {value ? moment(value).format('DD-MMM-YYYY') : '-'}
        </Typography>
        <Typography style={{ fontSize: '13px', color: '#888' }}>
            {value ? moment(value).format('hh:mm A') : ''}
        </Typography>
    </>
);

const reasonText = (failedByReason) =>
    Object.entries(failedByReason ?? {})
        .map(([reason, count]) => `${reason}: ${count}`)
        .join(' · ');

// Daytime uploads run as 10k-row chunks; each finished chunk has its own result file
function ChunkList({ uploadId, chunksDone, dispatchFetchChunks, dispatchFetchChunkResult }) {
    const [chunks, setChunks] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        dispatchFetchChunks(
            uploadId,
            (resp) => {
                setLoadFailed(false);
                setChunks(resp?.response?.chunks ?? []);
            },
            () => {
                setLoadFailed(true);
                setChunks([]);
            },
        );
        // Refetch when the parent row's progress moves so finished chunks show their download
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadId, chunksDone]);

    if (chunks === null) return <Typography style={{ fontSize: '13px', color: '#888' }}>Loading chunks…</Typography>;
    if (chunks.length === 0) {
        return (
            <Typography style={{ fontSize: '13px', color: loadFailed ? '#c62828' : '#888' }}>
                {loadFailed ? 'Could not load chunks. Press Refresh to try again.' : 'No chunks yet'}
            </Typography>
        );
    }

    return (
        <Box style={{ width: '100%', marginTop: 8 }}>
            {chunks.map((chunk) => (
                <Box
                    key={chunk.chunkNo}
                    style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderTop: '1px dashed #ebedf8' }}
                >
                    <Typography style={{ flex: '0 0 22%', fontSize: '13px' }}>
                        Chunk {chunk.chunkNo} · rows {chunk.rowStart}–{chunk.rowEnd}
                    </Typography>
                    <Box style={{ flex: '0 0 14%' }}>
                        <StatusChip status={chunk.status} />
                    </Box>
                    <Typography style={{ flex: '1 1 0', fontSize: '13px', color: '#555', paddingRight: 8 }}>
                        {chunk.acceptedRows != null
                            ? `${chunk.acceptedRows} accepted · ${chunk.failedRows} failed${
                                  reasonText(chunk.failedByReason) ? ` (${reasonText(chunk.failedByReason)})` : ''
                              }`
                            : 'In queue'}
                    </Typography>
                    {chunk.resultAvailable ? (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() =>
                                dispatchFetchChunkResult(uploadId, chunk.chunkNo, (resp) => {
                                    const url = resp?.response?.resultFileUrl;
                                    if (url) downloadData(url, url.split('/').pop());
                                })
                            }
                        >
                            Download
                        </Button>
                    ) : (
                        <Typography style={{ fontSize: '13px', color: '#888' }}>Not ready</Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}

function BulkUploadHistoryModal({
    open,
    onClose,
    dispatchFetchBulkUploadList,
    dispatchFetchChunks,
    dispatchFetchChunkResult,
}) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expanded, setExpanded] = useState(null);

    const fetchList = useCallback(
        (pageNo, pageSize) => {
            setLoading(true);
            dispatchFetchBulkUploadList(
                {
                    startDate: moment()
                        .subtract(29, 'days')
                        .format('YYYY-MM-DD'),
                    endDate: moment().format('YYYY-MM-DD'),
                    pageNo,
                    pageSize,
                },
                (resp) => {
                    setLoading(false);
                    setRows(resp?.response?.uploads ?? []);
                    setHasMore(resp?.response?.hasMore ?? false);
                },
                () => setLoading(false),
            );
        },
        [dispatchFetchBulkUploadList],
    );

    useEffect(() => {
        if (open) fetchList(page + 1, rowsPerPage);
    }, [open, page, rowsPerPage, fetchList]);

    const handleClose = () => {
        setPage(0);
        setRows([]);
        onClose && onClose();
    };

    const actions = [
        <Button
            key="refresh"
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={() => fetchList(page + 1, rowsPerPage)}
            disableElevation
        >
            Refresh
        </Button>,
        <Button
            key="close"
            variant="contained"
            color="default"
            className="cancelButton"
            onClick={handleClose}
            disableElevation
        >
            Close
        </Button>,
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Bulk Upload History"
            subtitle="Last 30 days. Chunked uploads publish a result file per 10,000-row chunk as soon as that chunk finishes."
            actions={actions}
            maxWidth="lg"
        >
            {loading && <Spinner />}

            {!loading && rows.length === 0 && (
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '32px 0',
                    }}
                >
                    <EmptyIcon />
                    <Typography style={{ marginTop: 12, color: '#888' }}>
                        No bulk uploads yet
                    </Typography>
                </Box>
            )}

            {!loading && rows.length > 0 && (
                <>
                    <List component="nav">
                        <ListItem className={classes.listItemHeader}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%',
                                }}
                            >
                                <Box style={{ flex: '0 0 16%' }}>
                                    Uploaded At
                                </Box>
                                <Box style={{ flex: '0 0 15%' }}>Status</Box>
                                <Box style={{ flex: '0 0 20%' }}>Rows</Box>
                                <Box style={{ flex: '0 0 16%' }}>
                                    Completed At
                                </Box>
                                <Box style={{ flex: '0 0 15%' }}>
                                    Input File
                                </Box>
                                <Box style={{ flex: '1 1 0' }}>Result</Box>
                            </Box>
                        </ListItem>

                        {rows.map((row, index) => (
                            <ListItem
                                key={row.uploadId ?? index}
                                className={classes.listItem}
                                style={{
                                    flexWrap: 'wrap',
                                    ...(index % 2 ? { backgroundColor: 'rgba(241, 243, 246, 1)' } : {}),
                                }}
                            >
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        width: '100%',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        style={{
                                            flex: '0 0 16%',
                                            paddingRight: 8,
                                        }}
                                    >
                                        <DateTimeCell value={row.uploadedAt} />
                                    </Box>
                                    <Box
                                        style={{
                                            flex: '0 0 15%',
                                            paddingRight: 8,
                                        }}
                                    >
                                        <StatusChip status={row.status} />
                                        {row.errorMessage && (
                                            <Typography
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#c62828',
                                                    wordBreak: 'break-word',
                                                    marginTop: 4,
                                                }}
                                            >
                                                {row.errorMessage}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        style={{
                                            flex: '0 0 20%',
                                            paddingRight: 8,
                                        }}
                                    >
                                        <Typography style={{ fontSize: '14px' }}>
                                            {row.totalRows ?? '-'} total
                                        </Typography>
                                        {row.chunkCount != null && (
                                            <Typography style={{ fontSize: '13px', color: '#1565c0' }}>
                                                {row.chunksDone ?? 0} of {row.chunkCount} chunks done ·{' '}
                                                {row.pendingRows ?? 0} pending
                                            </Typography>
                                        )}
                                        <Typography
                                            style={{
                                                fontSize: '13px',
                                                color: '#888',
                                            }}
                                        >
                                            {row.successRows ?? '-'} pass ·{' '}
                                            {row.failedRows ?? '-'} fail
                                        </Typography>
                                        {reasonText(row.failedByReason) && (
                                            <Typography style={{ fontSize: '12px', color: '#888', wordBreak: 'break-word' }}>
                                                {reasonText(row.failedByReason)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        style={{
                                            flex: '0 0 16%',
                                            paddingRight: 8,
                                        }}
                                    >
                                        <DateTimeCell value={row.completedAt} />
                                    </Box>
                                    <Box
                                        style={{
                                            flex: '0 0 15%',
                                            paddingRight: 8,
                                        }}
                                    >
                                        {row.inputFileUrl ? (
                                            <Link
                                                style={{
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() =>
                                                    downloadData(
                                                        row.inputFileUrl,
                                                        row.inputFileUrl
                                                            .split('/')
                                                            .pop(),
                                                    )
                                                }
                                            >
                                                Download
                                            </Link>
                                        ) : (
                                            <Typography
                                                style={{ fontSize: '13px' }}
                                            >
                                                -
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box style={{ flex: '1 1 0' }}>
                                        {row.chunkCount != null ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                onClick={() =>
                                                    setExpanded(expanded === row.uploadId ? null : row.uploadId)
                                                }
                                            >
                                                {expanded === row.uploadId ? 'Hide chunks' : 'View chunks'}
                                            </Button>
                                        ) : row.outputFileUrl ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                onClick={() =>
                                                    downloadData(
                                                        row.outputFileUrl,
                                                        row.outputFileUrl
                                                            .split('/')
                                                            .pop(),
                                                    )
                                                }
                                            >
                                                Download Result
                                            </Button>
                                        ) : (
                                            <Typography
                                                style={{
                                                    fontSize: '13px',
                                                    color: '#888',
                                                }}
                                            >
                                                Not ready
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {expanded === row.uploadId && (
                                    <ChunkList
                                        uploadId={row.uploadId}
                                        chunksDone={row.chunksDone}
                                        dispatchFetchChunks={dispatchFetchChunks}
                                        dispatchFetchChunkResult={dispatchFetchChunkResult}
                                    />
                                )}
                            </ListItem>
                        ))}
                    </List>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[10, 50, 100]}
                        count={
                            hasMore
                                ? (page + 2) * rowsPerPage
                                : page * rowsPerPage + rows.length
                        }
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onChangePage={(_, newPage) => setPage(newPage)}
                        onChangeRowsPerPage={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </>
            )}
        </Modal>
    );
}

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchBulkUploadList: (data, onSuccess, onError) =>
        dispatch(fetchAgencyBulkUploadList(data, onSuccess, onError)),
    dispatchFetchChunks: (uploadId, onSuccess, onError) =>
        dispatch(fetchAgencyUploadChunks(uploadId, onSuccess, onError)),
    dispatchFetchChunkResult: (uploadId, chunkNo, onSuccess, onError) =>
        dispatch(fetchAgencyChunkResult(uploadId, chunkNo, onSuccess, onError)),
});

export default connect(null, mapDispatchToProps)(BulkUploadHistoryModal);
